import { useCallback, useEffect, useState } from "react";
import { applyImageWidthPercent, readImageWidthPercent } from "./descriptionImageUtils";

/**
 * Click an inline description image to preview and resize; sync HTML via onContentSync on Apply.
 * @param {HTMLElement|null} editorRoot
 * @param {{ enabled?: boolean, onContentSync?: () => void }} options
 */
export function useEditorImageResize(editorRoot, { enabled = true, onContentSync } = {}) {
  const [modalState, setModalState] = useState(null);

  useEffect(() => {
    if (!enabled || !editorRoot) return undefined;

    const handleClick = (event) => {
      const target = event.target;
      if (target?.tagName !== "IMG") return;
      if (!editorRoot.contains(target)) return;
      event.preventDefault();
      event.stopPropagation();
      setModalState({
        img: target,
        src: target.src,
        widthPercent: readImageWidthPercent(target, editorRoot),
      });
    };

    editorRoot.addEventListener("click", handleClick);
    return () => editorRoot.removeEventListener("click", handleClick);
  }, [enabled, editorRoot]);

  const closeImageModal = useCallback(() => setModalState(null), []);

  const applyImageWidth = useCallback(
    (widthPercent) => {
      if (!modalState?.img) return;
      applyImageWidthPercent(modalState.img, widthPercent);
      onContentSync?.();
      setModalState(null);
    },
    [modalState, onContentSync]
  );

  return {
    imageModalOpen: Boolean(modalState),
    imageModalSrc: modalState?.src ?? "",
    imageModalWidthPercent: modalState?.widthPercent ?? 100,
    closeImageModal,
    applyImageWidth,
  };
}
